"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SwapCalculation = exports.BETTER_TRADE_LESS_HOPS_THRESHOLD = void 0;
const ethers_1 = require("ethers");
const axios_1 = require("axios");
const bignumber_js_1 = require("bignumber.js");
const ethers_multicall_1 = require("ethers-multicall");
const constant_1 = require("../../constant");
const sdk_1 = require("@pancakeswap/sdk");
const utils_1 = require("./utils");
const AMMFactory_1 = require("../abi/AMMFactory");
const AMMPair_1 = require("../abi/AMMPair");
const peFactory_1 = require("../abi/peFactory");
const types_1 = require("./types");
const API_HOSTNAME = {
    [types_1.ApiENV.QC]: 'https://apex-qc.nonprodposi.com/position-exchange/v1/',
    [types_1.ApiENV.UAT]: 'https://apex-uat.position.exchange/position-exchange/v1/',
    [types_1.ApiENV.PROD]: 'https://apex.position.exchange/position-exchange/v1/'
};
const PE_KEY = 'position_exchange';
function getTokenABKey(tokenA, tokenB) {
    return Number(tokenA) < Number(tokenB) ? `${tokenA.toLowerCase()}-${tokenB.toLowerCase()}` : `${tokenB.toLowerCase()}-${tokenA.toLowerCase()}`;
}
function compareTokenAddress(a, b) {
    return a.toLowerCase() === b.toLowerCase();
}
const MAX_HOPS = 3;
exports.BETTER_TRADE_LESS_HOPS_THRESHOLD = new sdk_1.Percent(sdk_1.JSBI.BigInt(50), sdk_1.JSBI.BigInt(10000));
class SwapCalculation {
    constructor(networkId, apiEnv, customRPC) {
        this.initialized = {};
        this.latestFetchTData = {};
        this.AMMReserve = new AMMReserve();
        this.PEOrderbook = new TokenABReserve();
        this.cacheContract = {};
        this.enableTiming = false;
        this.commonPairs = [];
        this.provider = new ethers_1.ethers.providers.JsonRpcProvider(customRPC || constant_1.RPC_ENDPOINT[networkId]);
        this.networkId = networkId;
        this.networkConfig = new utils_1.NetworkConfig(this.networkId, customRPC);
        this.apiHostname = API_HOSTNAME[apiEnv];
        if (!this.apiHostname) {
            throw Error(`No support api env: ${apiEnv}`);
        }
    }
    setCommonPairs(_pairs) {
        if (!_pairs)
            return;
        this.commonPairs = _pairs;
    }
    init(exchanges) {
        return __awaiter(this, void 0, void 0, function* () {
            this.compareExchanges = exchanges;
            this.multicallProvider = new ethers_multicall_1.Provider(this.networkConfig.getProvider());
            yield this.multicallProvider.init();
            this.cacheContract[`peSpotFactory`] = new ethers_1.ethers.Contract(this.networkConfig.getFactory(PE_KEY), peFactory_1.default, this.networkConfig.getProvider());
            for (const exchange of [...this.compareExchanges, 'position_exchange_amm']) {
                this.cacheContract[`${exchange}_factory`] = new ethers_1.ethers.Contract(this.networkConfig.getFactory(exchange), AMMFactory_1.default, this.networkConfig.getProvider());
            }
        });
    }
    fetchData(tokenIn, tokenOut, refetchTimeInSeconds = 5) {
        return __awaiter(this, void 0, void 0, function* () {
            tokenIn = tokenIn.toLowerCase();
            tokenOut = tokenOut.toLowerCase();
            const abTokenKey = getTokenABKey(tokenIn, tokenOut);
            if (!this.compareExchanges)
                throw Error(`Call to .init(exchanges) before fetchData`);
            if (this.latestFetchTData[abTokenKey] && this.latestFetchTData[abTokenKey] > 0 && Date.now() - this.latestFetchTData[abTokenKey] < refetchTimeInSeconds * 1000) {
                return;
            }
            this._startTime(`Fetch PE`);
            const task1 = new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                const pePairManagerAddress = yield this.getPEPairAddress(tokenIn, tokenOut);
                if (!pePairManagerAddress) {
                    yield this._setReservePE(tokenIn, tokenOut).then(resolve).catch(reject);
                }
                else {
                    try {
                        const [orderbookRaw, { baseAssetAddress, spotHouse }] = yield Promise.all([
                            this.getOrderbook(pePairManagerAddress),
                            this.getBaseAsset(pePairManagerAddress)
                        ]);
                        this.PEOrderbook.set(tokenIn, tokenOut, Object.assign(Object.assign({}, orderbookRaw), { baseAssetAddress,
                            spotHouse }));
                    }
                    catch (error) {
                        console.error(`Fetching Orderbook Error:`, error);
                        yield this._setReservePE(tokenIn, tokenOut);
                    }
                    finally {
                        resolve(null);
                    }
                }
            }));
            this._endTime(`Fetch PE`);
            const task2 = new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                try {
                    for (const exchange of this.compareExchanges) {
                        this._startTime(`Fetch ${exchange}`);
                        const reserveData = yield this.getReserves(exchange, { tokenA: tokenIn, tokenB: tokenOut });
                        this.AMMReserve.set(exchange, { tokenA: tokenIn, tokenB: tokenOut }, reserveData);
                        this._endTime(`Fetch ${exchange}`);
                    }
                    resolve(null);
                }
                catch (e) {
                    reject(e);
                }
            }));
            yield Promise.all([task1, task2]);
            if (!this.initialized[abTokenKey])
                this.initialized[abTokenKey] = true;
            this.latestFetchTData[abTokenKey] = Date.now();
            return true;
        });
    }
    _setReservePE(tokenIn, tokenOut) {
        return __awaiter(this, void 0, void 0, function* () {
            this.AMMReserve.set('position_exchange_amm', { tokenA: tokenIn, tokenB: tokenOut }, {
                [tokenIn]: '0',
                [tokenOut]: '0'
            });
        });
    }
    computeOutput(quantity, swapTokenIn, swapTokenOut, slippageTolerance, tokenInDecimals = 18) {
        const tokenIn = swapTokenIn.address.toLowerCase();
        const tokenOut = swapTokenOut.address.toLowerCase();
        if (!this.initialized[getTokenABKey(tokenIn, tokenOut)]) {
            throw new Error(`No initialized. Need to call fetchData first`);
        }
        let positionExchangeResult = {};
        const computeData = this.PEOrderbook.get(tokenIn, tokenOut);
        if (typeof computeData !== 'undefined') {
            const orderbookRaw = computeData;
            orderbookRaw.bids = orderbookRaw.bids.reverse();
            const isBuy = computeData.baseAssetAddress.toLowerCase() == tokenOut.toLowerCase();
            const rawAmount = new bignumber_js_1.default(quantity.toString()).div(Math.pow(10, tokenInDecimals)).toNumber();
            const functionHandler = isBuy ? utils_1.estimateBaseAmountFromObByQuote : utils_1.estimateQuoteAmountFromOB;
            const receiveAmount = new bignumber_js_1.default(functionHandler(rawAmount, isBuy, (0, utils_1.orderbookRawToObItem)(orderbookRaw))).multipliedBy(Math.pow(10, tokenInDecimals));
            const midPrice = new bignumber_js_1.default(orderbookRaw[isBuy ? 'asks' : 'bids'][0][0]);
            const { slippage, priceInput, priceOutput } = this._computeSlippage(midPrice, new bignumber_js_1.default(quantity.toString()), receiveAmount.toString(), 0, isBuy);
            const fee = this.networkConfig.getFee(PE_KEY);
            const feeAmount = receiveAmount.times(fee);
            positionExchangeResult.spotHouse = computeData.spotHouse;
            positionExchangeResult.receiveAmount = receiveAmount.minus(fee).dp(0, bignumber_js_1.default.ROUND_UP).toString();
            positionExchangeResult.slippage = slippage;
            positionExchangeResult.fee = feeAmount.toString();
            positionExchangeResult.minimumReceive = receiveAmount.times(1 - slippageTolerance / 100).toString();
            positionExchangeResult.priceInput = priceInput;
            positionExchangeResult.priceOutput = priceOutput;
        }
        else {
            positionExchangeResult = this._computeAMMOutput(quantity, 'position_exchange_amm', swapTokenIn, swapTokenOut, slippageTolerance);
        }
        const competitor = {};
        for (const exchange of this.compareExchanges) {
            competitor[exchange] = this._computeAMMOutput(quantity, exchange, swapTokenIn, swapTokenOut, slippageTolerance);
        }
        return {
            PositionExchange: positionExchangeResult,
            competitor
        };
    }
    getOrderbook(pairManager) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = `${this.apiHostname}order-books/${pairManager}`;
            return axios_1.default.get(url).then(res => res.data.data);
        });
    }
    getReserves(exchange, { tokenA, tokenB }) {
        return __awaiter(this, void 0, void 0, function* () {
            const existPair = this.commonPairs.find((pair) => {
                return (compareTokenAddress(pair.token0.address, tokenA) && compareTokenAddress(pair.token1.address, tokenB)) || (compareTokenAddress(pair.token0.address, tokenB) && compareTokenAddress(pair.token1.address, tokenA));
            });
            if (existPair) {
                return {
                    [compareTokenAddress(existPair.token0.address, tokenA) ? tokenA : tokenB]: existPair.reserve0.raw.toString(),
                    [compareTokenAddress(existPair.token1.address, tokenA) ? tokenA : tokenB]: existPair.reserve1.raw.toString(),
                };
            }
            let pairAddress = CacheAMMPairAddress.get(exchange, tokenA, tokenB);
            if (!pairAddress) {
                this._startTime(`Setup pairAddress`);
                const factory = this.cacheContract[`${exchange}_factory`];
                if (!factory)
                    throw new Error(`No init factory ${exchange}`);
                pairAddress = yield factory.getPair(tokenA, tokenB);
                CacheAMMPairAddress.set(exchange, tokenA, tokenB, pairAddress);
                this._endTime(`Setup pairAddress`);
            }
            if (pairAddress === ethers_1.ethers.constants.AddressZero) {
                return {
                    [tokenA.toLowerCase()]: '0',
                    [tokenB.toLowerCase()]: '0',
                };
            }
            const pairContract = new ethers_multicall_1.Contract(pairAddress, AMMPair_1.default);
            this._startTime(`Calling Multicall`);
            const [{ _reserve0, _reserve1 }, token0, token1] = yield this.multicallProvider.all([pairContract.getReserves(), pairContract.token0(), pairContract.token1()]);
            this._endTime(`Calling Multicall`);
            return {
                [token0.toLowerCase()]: _reserve0.toString(),
                [token1.toLowerCase()]: _reserve1.toString(),
            };
        });
    }
    getAmmAmountOut(amountIn, reserveIn, reserveOut, fee) {
        const _amountIn = new bignumber_js_1.default(amountIn.toString());
        const _reserveIn = new bignumber_js_1.default(reserveIn.toString());
        const amountInWithFee = _amountIn.times(1 - fee);
        const numerator = amountInWithFee.times(reserveOut.toString());
        const denominator = _reserveIn.plus(amountInWithFee);
        return numerator.div(denominator).dp(0).toString();
    }
    getPEPairAddress(tokenA, tokenB) {
        return __awaiter(this, void 0, void 0, function* () {
            const cache = CacheAMMPairAddress.get(PE_KEY, tokenA, tokenB);
            if (cache) {
                return cache;
            }
            const spotFactoryContract = this.cacheContract[`peSpotFactory`];
            const { pairManager } = yield spotFactoryContract.getPairManagerSupported(tokenA, tokenB);
            if (pairManager === ethers_1.ethers.constants.AddressZero) {
                return null;
            }
            CacheAMMPairAddress.set(PE_KEY, tokenA, tokenB, pairManager);
            return pairManager;
        });
    }
    getBaseAsset(pairManager) {
        return __awaiter(this, void 0, void 0, function* () {
            this._startTime(`Get baseAsset`);
            const key = `${PE_KEY}_info:v2.0:${pairManager}`;
            const result = yield this._getAddressFromCacheOrFetch(key, () => __awaiter(this, void 0, void 0, function* () {
                const url = `${this.apiHostname}pairs/${pairManager}`;
                const data = yield axios_1.default.get(url).then(res => res.data).catch(err => {
                    throw err;
                });
                if ((data === null || data === void 0 ? void 0 : data.meta.code) === 200) {
                    const { data: pairData } = data;
                    return {
                        baseAssetAddress: pairData.base.contract_address,
                        spotHouse: pairData.spot_house_address
                    };
                }
                else {
                    throw new Error(`Response code ${data === null || data === void 0 ? void 0 : data.meta.code}`);
                }
            }));
            this._endTime(`Get baseAsset`);
            return result;
        });
    }
    _getAddressFromCacheOrFetch(key, callback) {
        return __awaiter(this, void 0, void 0, function* () {
            const cache = CacheAddress.get(key);
            if (cache) {
                return cache;
            }
            const data = yield callback();
            CacheAddress.set(key, data);
            return data;
        });
    }
    _computeAMMOutput(quantity, exchange, tokenIn, tokenOut, slippageTolerance) {
        var _a;
        const reserveData = this.AMMReserve.get(exchange, tokenIn.address, tokenOut.address);
        const reserveIn = reserveData[tokenIn.address.toLowerCase()];
        const reserveOut = reserveData[tokenOut.address.toLowerCase()];
        let amountOut = '0';
        let midPrice = new bignumber_js_1.default(0);
        const fee = this.networkConfig.getFee(exchange);
        if (reserveIn === '0' && reserveOut === '0' || exchange !== 'position_exchange_amm') {
            if (this.commonPairs.length > 0) {
                let bestTradeSoFar = null;
                for (let i = 1; i <= MAX_HOPS; i++) {
                    const currentTrade = (_a = sdk_1.Trade.bestTradeExactIn(this.commonPairs, new sdk_1.TokenAmount(tokenIn, quantity.toString()), tokenOut, { maxHops: i, maxNumResults: 1 })[0]) !== null && _a !== void 0 ? _a : null;
                    if ((0, utils_1.isTradeBetter)(bestTradeSoFar, currentTrade, exports.BETTER_TRADE_LESS_HOPS_THRESHOLD)) {
                        bestTradeSoFar = currentTrade;
                    }
                }
                if (bestTradeSoFar) {
                    amountOut = bestTradeSoFar.outputAmount.raw.toString();
                    midPrice = new bignumber_js_1.default(bestTradeSoFar.nextMidPrice.raw.toSignificant(6));
                }
            }
            else {
                throw new Error(`This pair does not exist. Need to trade though routes, please set commonPairs`);
            }
        }
        else {
            amountOut = this.getAmmAmountOut(quantity, reserveIn, reserveOut, fee);
            midPrice = new bignumber_js_1.default(reserveOut).div(reserveIn);
        }
        const quantityBN = new bignumber_js_1.default(quantity.toString());
        const { slippage, priceInput, priceOutput } = this._computeSlippage(midPrice, quantityBN, amountOut, fee);
        const feeAmount = quantityBN.times(fee).toString();
        const minimumReceive = new bignumber_js_1.default(amountOut).times(1 - slippageTolerance / 100).toString();
        return {
            receiveAmount: amountOut.replace('-', ''),
            slippage,
            fee: feeAmount,
            priceInput,
            priceOutput,
            minimumReceive
        };
    }
    _computeSlippage(midPrice, quantityBN, amountOut, fee, isBuy) {
        let currentOut;
        if (typeof isBuy !== 'undefined' && isBuy) {
            currentOut = quantityBN.div(midPrice).times(1 - fee);
        }
        else {
            currentOut = midPrice.times(quantityBN).times(1 - fee);
        }
        const avgOut = currentOut.plus(amountOut).div(2);
        const priceInput = quantityBN.div(avgOut).dp(8).toString();
        const priceOutput = avgOut.div(quantityBN).dp(8).toString();
        const slippage = currentOut.minus(amountOut).div(currentOut).abs().toNumber();
        return {
            slippage,
            priceInput,
            priceOutput
        };
    }
    _startTime(label) {
        this.enableTiming && console.time(label);
    }
    _endTime(label) {
        this.enableTiming && console.timeEnd(label);
    }
}
exports.SwapCalculation = SwapCalculation;
class CacheAddress {
    static get(key) {
        if (typeof localStorage === 'undefined')
            return undefined;
        const data = localStorage.getItem(key);
        try {
            return JSON.parse(data);
        }
        catch (error) {
            return data;
        }
    }
    static set(key, value) {
        if (typeof localStorage === 'undefined')
            return;
        if (typeof value === 'object') {
            value = JSON.stringify(value);
        }
        localStorage.setItem(key, value);
    }
}
class CacheAMMPairAddress {
    static get(exchange, tokenA, tokenB) {
        return CacheAddress.get(`PAIR_ADDRESS:${exchange}:${getTokenABKey(tokenA, tokenB)}`);
    }
    static set(exchange, tokenA, tokenB, pairAddress) {
        CacheAddress.set(`PAIR_ADDRESS:${exchange}:${getTokenABKey(tokenA, tokenB)}`, pairAddress);
    }
}
class AMMReserve {
    constructor() {
        this.data = {};
    }
    set(exchange, { tokenA, tokenB }, reserveData) {
        if (!this.data[exchange]) {
            this.data[exchange] = new TokenABReserve();
        }
        this.data[exchange].set(tokenA, tokenB, reserveData);
    }
    get(exchange, tokenIn, tokenOut) {
        return this.data[exchange].get(tokenIn, tokenOut);
    }
}
class TokenABReserve {
    constructor() {
        this.data = new Map();
    }
    set(tokenA, tokenB, data) {
        this.data.set(this.getKey(tokenA, tokenB), data);
    }
    get(tokenA, tokenB) {
        return this.data.get(this.getKey(tokenA, tokenB));
    }
    getKey(tokenA, tokenB) {
        tokenA = tokenA.toLowerCase();
        tokenB = tokenB.toLowerCase();
        const keyA = `${tokenA}_${tokenB}`;
        const keyB = `${tokenB}_${tokenA}`;
        if (this.data.has(keyB)) {
            return keyB;
        }
        return keyA;
    }
}
//# sourceMappingURL=swap.js.map